import { ArrowRight, Star } from 'lucide-react';

import Button from 'shared/components/button/Button';
import CombinedTitle from 'shared/components/title/CombinedTitle';
import { SPACES } from 'shared/utils/constants';
import { SPACES_ROUTE } from 'app/navigation/routes/space-routes';
import { useCallback } from 'react';
import { useNavigationContext } from 'shared/providers/NavigationProvider';
import { useTranslation } from 'react-i18next';

const WhoWeAreSection = () => {

    const { t } = useTranslation();
    const { navigateToRoute, navigateToRouteWithState } = useNavigationContext();

    const handleNavigateToSpacesPage = useCallback(
        () => navigateToRoute(SPACES_ROUTE),
        [navigateToRoute]
    );

    const handleNavigateToSpaceDetailsPage = useCallback(
        (spaceId) => {
            return () => navigateToRouteWithState({
                path: `${SPACES_ROUTE}/${spaceId}`,
                state: spaceId
            });
        },
        [navigateToRouteWithState]
    );

    return (
        <section className="flex w-full max-w-6xl mx-auto flex-col px-2 md:px-6 pb-10" id='about'>
            {/* Introduction Section */}
            <CombinedTitle
                intro={t('website.whoWeAre.intro')}
                title={t('website.whoWeAre.title')}
                subtitle={t('website.whoWeAre.subtitle')}
            />

            {/* Cards Section */}
            <div className="w-full overflow-x-scroll scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 py-2 px-2 md:px-4">
                <div className="flex gap-4 w-max">
                    {SPACES.map((space, index) => (
                        <SpaceCard
                            key={space.id}
                            image={space.img}
                            title={space.title}
                            location={space.location}
                            rating={space.rating}
                            price={75}
                            discount={12}
                            onClick={handleNavigateToSpaceDetailsPage(space.id)}
                        />
                    ))}
                </div>
            </div>

            {/* Explore More Button */}
            <div className=' px-2 md:px-4 mt-6 '>
                <Button
                    onClick={handleNavigateToSpacesPage}
                    className='bg-lime-700 text-white py-3 px-4 rounded-md hover:bg-lime-800'
                >
                    {t('website.whoWeAre.exploreMore')}
                    <ArrowRight className="inline-block w-5 h-5 ml-2" />
                </Button>
            </div>
        </section>
    );
};

const SpaceCard = ({ image, title, location, rating, onClick }) => {
    const { t } = useTranslation();

    return (
        <div
            onClick={onClick}
            className="w-[260px] bg-white rounded-md overflow-hidden cursor-pointer  transform transition-transform duration-300 hover:scale-[1.05]"
        >

            {/* Image Section */}
            <div className="relative w-full h-[250px]">
                <img
                    src={image}
                    alt={title}
                    className="w-full h-full object-cover rounded-md"
                />

                {/* View Details Overlay */}
                <div className="absolute bottom-3 right-3 bg-black/40 text-white text-xs px-3 py-1 rounded-full backdrop-blur-sm">
                    {t('website.whoWeAre.viewDetails')}
                </div>
            </div>

            {/* Content */}
            <div className="py-4 ">
                <div className="flex justify-between items-center mb-1 ">
                    <h3 className="font-semibold text-gray-900 text-lg">{title}</h3>
                    <div className="flex items-center">
                        <span className="text-sm text-gray-600">{rating}</span>
                        <Star size={14} className="inline-block text-yellow-500 ml-1" />
                    </div>
                </div>

                <div className="flex items-center text-gray-500 text-sm">
                    {location}
                </div>
            </div>

        </div>
    );
};

export default WhoWeAreSection;
